/**
 * In-app proof video recorder (spec §9.5): getUserMedia + MediaRecorder
 * behind feature detection, a hard 60-second auto-stop, full stream cleanup
 * on stop/retake/unmount, and Dutch feedback for permission failures.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  PROOF_MAX_DURATION_SECONDS,
  extensionForMimeType,
  isInAppRecordingSupported,
  pickSupportedMimeType,
} from './limits'

export type RecorderPhase =
  | 'idle'
  | 'requesting'
  | 'ready'
  | 'recording'
  | 'recorded'
  | 'permission_denied'
  | 'no_camera'
  | 'error'

export type RecordedProof = {
  blob: Blob
  mimeType: string
  durationSeconds: number
}

const RECORDER_PHASE_COPY: Record<Exclude<RecorderPhase, 'idle' | 'requesting' | 'ready' | 'recording' | 'recorded'>, string> = {
  permission_denied:
    'Geen toestemming voor camera en microfoon. Geef toestemming in je browserinstellingen of kies een videobestand.',
  no_camera: 'Er is geen camera gevonden op dit apparaat. Kies in plaats daarvan een videobestand.',
  error: 'De camera kan niet worden gestart. Probeer het opnieuw of kies een videobestand.',
}

export function recorderPhaseMessage(phase: RecorderPhase): string | null {
  if (phase === 'permission_denied' || phase === 'no_camera' || phase === 'error') {
    return RECORDER_PHASE_COPY[phase]
  }
  return null
}

export function useProofRecorder() {
  const supported = isInAppRecordingSupported()
  const [phase, setPhase] = useState<RecorderPhase>('idle')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [recording, setRecording] = useState<RecordedProof | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const previewUrlRef = useRef<string | null>(null)
  const mountedRef = useRef(true)
  /** Bumped on retake/reset so a pending getUserMedia resolves as cancelled. */
  const generationRef = useRef(0)

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    setPreviewUrl(null)
  }, [])

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
    stopTimer()
  }, [stopTimer])

  const requestCamera = useCallback(async () => {
    if (!supported || phase === 'requesting') return
    setPhase('requesting')
    const generation = generationRef.current
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true,
      })
      if (!mountedRef.current || generation !== generationRef.current) {
        // Cancelled (retake/close) while the permission prompt was open.
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      streamRef.current = stream
      setPhase('ready')
    } catch (error) {
      if (!mountedRef.current) return
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
          setPhase('permission_denied')
          return
        }
        if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
          setPhase('no_camera')
          return
        }
      }
      setPhase('error')
    }
  }, [supported, phase])

  const startRecording = useCallback(() => {
    const stream = streamRef.current
    if (!stream || phase !== 'ready') return
    const mimeType = pickSupportedMimeType() ?? ''
    try {
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'video/webm'
        const blob = new Blob(chunksRef.current, { type })
        const durationSeconds = Math.min(
          PROOF_MAX_DURATION_SECONDS,
          (Date.now() - startedAtRef.current) / 1000,
        )
        stopStream()
        recorderRef.current = null
        if (!mountedRef.current) return
        revokePreview()
        const url = URL.createObjectURL(blob)
        previewUrlRef.current = url
        setPreviewUrl(url)
        setRecording({ blob, mimeType: type, durationSeconds })
        setPhase('recorded')
      }
      recorderRef.current = recorder
      startedAtRef.current = Date.now()
      setElapsedSeconds(0)
      recorder.start(1000)
      setPhase('recording')
      timerRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startedAtRef.current) / 1000
        setElapsedSeconds(Math.min(PROOF_MAX_DURATION_SECONDS, elapsed))
        if (elapsed >= PROOF_MAX_DURATION_SECONDS) {
          // Hard product limit: stop exactly at 60 seconds.
          stopRecording()
        }
      }, 250)
    } catch {
      stopStream()
      if (mountedRef.current) setPhase('error')
    }
  }, [phase, revokePreview, stopRecording, stopStream])

  /** Discard the take and return to the live camera view (opnieuw opnemen). */
  const retake = useCallback(() => {
    generationRef.current += 1
    stopRecording()
    stopStream()
    revokePreview()
    setRecording(null)
    setElapsedSeconds(0)
    setPhase('idle')
  }, [revokePreview, stopRecording, stopStream])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopRecording()
      stopStream()
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    }
  }, [stopRecording, stopStream])

  const stream = streamRef.current

  return {
    supported,
    phase,
    stream,
    elapsedSeconds,
    recording,
    previewUrl,
    requestCamera,
    startRecording,
    stopRecording,
    retake,
  }
}

/** Builds a well-named File from an in-app recording for attachProof. */
export function recordingToFile(recording: RecordedProof): File {
  const extension = extensionForMimeType(recording.mimeType)
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return new File([recording.blob], `bewijsvideo-${stamp}.${extension}`, {
    type: recording.mimeType,
    lastModified: Date.now(),
  })
}
